import { apiClient } from '@/lib/api';
import type { ApproveWorkersResult, ImportIdentityReferencesReport, ImportWorkersReport, WorkerImportColumn, Worker, VerificationStage, AttendanceSession, AttendanceSessionDetail, AttendanceEntry, CompensationRule, Subcontractor, WorkerPaymentHistoryParams, WorkerPaymentHistoryResponse, NextOfKin, VerifyWorkerResponse, CheckInWorkerPayload, CheckOutWorkerPayload, CompensationPreviewResult } from '@/types/workforce.types';
import type { PaginatedResponse } from '@/types/compliance.types';

const BASE = '/workforce';

/**
 * What the identity reference data knows about a national ID. `found: false`
 * means we have never paid this person — not that the ID is invalid.
 */
export interface IdentityReferenceLookup {
  found: boolean;
  firstName: string | null;
  lastName: string | null;
  /**
   * Masked, e.g. `250 ••• ••• 419`. The real number is deliberately never
   * returned: if the form could fill it in, a matching phone number would
   * prove only that the registrar can read our database, and the verification
   * would be checking itself.
   */
  phoneNumberMasked: string | null;
  /**
   * What the payment sheet said about the work: the job title and the cost
   * centre it was booked against, folded onto the tenant's catalogues when
   * the sheet was imported. Not sensitive the way the number is — knowing
   * somebody was paid as a CLEANER proves nothing about their identity — so
   * the form offers them rather than making a registrar remember.
   */
  workerRoleId: string | null;
  position: string | null;
  costCentreId: string | null;
  costCentre: string | null;
  workingPlace: string | null;
}

/** An entry of a tenant-managed workforce list; today, the cost centres. */
export interface CostCentreOption {
  id: string;
  label: string;
  code: string;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
}

export interface CreateCostCentrePayload {
  label: string;
}

export interface UpdateCostCentrePayload {
  label: string;
}

const LABOUR_COST_CENTRE = 'LABOUR_COST_CENTRE';

export interface WorkerRoleOption {
  id: string;
  name: string;
  description?: string;
  isActive?: boolean;
}

export interface CreateWorkerRolePayload {
  name: string;
  description?: string;
}

export interface UpdateWorkerRolePayload {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface RegisterWorkerPayload {
  fullName: string;
  nationalIdNumber: string;
  phoneNumber: string;
  dateOfBirth: string;
  gender: string;
  workerCategory: string;
  workerRoleId?: string;
  customRoleName?: string;
  costCentreId?: string;
  customCostCentreName?: string;
  zoneId?: string;
  workingPlaceId?: string;
  teamId?: string;
  // FR-LBR-060 — relative share of team commission (omit for equal split).
  shareFactor?: number;
  dailyRate?: number | null;
  rssbNumber?: string | null;
  subcontractorId?: string;
  contractStartDate: string;
  contractType: string;
  bankAccountOrMobileMoney?: string;
  emergencyContact?: string;
  canWorkUnderground?: boolean;
  nationalIdDocumentUrl?: string;
  nationalIdBackUrl?: string;
  profilePhotoUrl?: string;
  nextOfKin?: NextOfKin;
  email?: string;
}

export interface UpdateWorkerPayload {
  fullName?: string;
  phoneNumber?: string;
  email?: string;
  dateOfBirth?: string;
  gender?: string;
  workerCategory?: string;
  workerRoleId?: string;
  customRoleName?: string;
  costCentreId?: string;
  customCostCentreName?: string;
  zoneId?: string;
  workingPlaceId?: string | null;
  teamId?: string | null;
  shareFactor?: number;
  dailyRate?: number | null;
  rssbNumber?: string | null;
  subcontractorId?: string;
  contractStartDate?: string;
  contractType?: string;
  bankAccountOrMobileMoney?: string;
  emergencyContact?: string;
  canWorkUnderground?: boolean;
  nextOfKin?: NextOfKin;
}

export interface WorkerImageUploadResponse {
  url: string;
  size: number;
  name: string;
}

export interface GetWorkersParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  workerCategory?: string;
  siteId?: string;
  levelId?: string;
  zoneId?: string;
  teamId?: string;
  unassignedOnly?: boolean;
}

export interface CreateAttendanceSessionPayload {
  date: string;
  shiftId: string;
  siteId: string;
  siteName: string;
  /** The zone, level and tunnel are sent unless a working place is sent in their place. */
  zoneId?: string;
  zoneName?: string;
  levelId?: string;
  levelName?: string;
  tunnelId?: string;
  tunnelName?: string;
  workingPlaceId?: string;
  teamId: string;
  teamName: string;
}

export interface GetAttendanceSessionsParams {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  siteId?: string;
  levelId?: string;
  zoneId?: string;
  tunnelId?: string;
  workingPlaceId?: string;
  shift?: string;
  shiftId?: string;
  status?: string;
}

export interface CreateCompensationRulePayload {
  name: string;
  compensationType: string;
  calculationBasis: string;
  rate: number;
  unit?: string;
  scopeType: string;
  scopeId?: string;
  paymentFrequency: string;
  effectiveStart: string;
  effectiveEnd?: string;
  stackingBehavior: string;
  payoutCap?: number;
}

export interface GetCompensationRulesParams {
  page?: number;
  limit?: number;
  compensationType?: string;
  search?: string;
  isActive?: boolean;
}

export interface CreateSubcontractorPayload {
  companyName: string;
  contactPerson: string;
  email: string;
  phoneNumber: string;
  contractReference?: string;
  // FR-LBR-050 — payment cadence for this subcontractor's workers.
  paymentFrequency?: string;
}

export interface GetSubcontractorsParams {
  page?: number;
  limit?: number;
  search?: string;
}

export const workforceService = {
  // ── Worker Roles ──

  async getCostCentres(): Promise<CostCentreOption[]> {
    const { data } = await apiClient.get<CostCentreOption[]>(
      `${BASE}/code-lists`,
      { params: { listKey: LABOUR_COST_CENTRE } },
    );
    return data;
  },

  async createCostCentre(
    payload: CreateCostCentrePayload,
  ): Promise<CostCentreOption> {
    const { data } = await apiClient.post<CostCentreOption>(
      `${BASE}/code-lists`,
      { ...payload, listKey: LABOUR_COST_CENTRE },
    );
    return data;
  },

  async updateCostCentre(
    id: string,
    payload: UpdateCostCentrePayload,
  ): Promise<CostCentreOption> {
    const { data } = await apiClient.patch<CostCentreOption>(
      `${BASE}/code-lists/${id}`,
      payload,
    );
    return data;
  },

  async deactivateCostCentre(id: string): Promise<CostCentreOption> {
    const { data } = await apiClient.post<CostCentreOption>(
      `${BASE}/code-lists/${id}/deactivate`,
      {},
    );
    return data;
  },

  async activateCostCentre(id: string): Promise<CostCentreOption> {
    const { data } = await apiClient.post<CostCentreOption>(
      `${BASE}/code-lists/${id}/activate`,
      {},
    );
    return data;
  },

  async getWorkerRoles(): Promise<WorkerRoleOption[]> {
    const { data } = await apiClient.get<WorkerRoleOption[]>(`${BASE}/roles`);
    return data;
  },

  // FR-ONB-003 — IT-Admin worker-role catalog management.
  async createWorkerRole(
    payload: CreateWorkerRolePayload,
  ): Promise<WorkerRoleOption> {
    const { data } = await apiClient.post<WorkerRoleOption>(
      `${BASE}/roles`,
      payload,
    );
    return data;
  },

  async updateWorkerRole(
    id: string,
    payload: UpdateWorkerRolePayload,
  ): Promise<WorkerRoleOption> {
    const { data } = await apiClient.patch<WorkerRoleOption>(
      `${BASE}/roles/${id}`,
      payload,
    );
    return data;
  },

  async deactivateWorkerRole(id: string): Promise<{ id: string; isActive: boolean }> {
    const { data } = await apiClient.delete<{ id: string; isActive: boolean }>(
      `${BASE}/roles/${id}`,
    );
    return data;
  },

  // SRS 7.7 — zones where the caller is the assigned engineer; used by the
  // Zone Engineer compensation-rule scope picker.
  async getZonesAssignedToMe(): Promise<{ id: string; name: string }[]> {
    const { data } = await apiClient.get<{ id: string; name: string }[]>(
      `${BASE}/zones/mine`,
    );
    return data;
  },

  // ── Workers ──

  /**
   * Looks a national ID up in the paid-identity reference data so the
   * registration form can fill itself in.
   *
   * Only ever called with a full sixteen digits — the endpoint refuses
   * anything shorter, deliberately, so this cannot be used to browse workers
   * by typing a prefix.
   */
  async lookupIdentityReference(
    nationalId: string,
  ): Promise<IdentityReferenceLookup> {
    const { data } = await apiClient.get<IdentityReferenceLookup>(
      `${BASE}/worker-identity-references/lookup`,
      { params: { nationalId } },
    );
    return data;
  },

  async registerWorker(payload: RegisterWorkerPayload): Promise<Worker> {
    const { data } = await apiClient.post<Worker>(`${BASE}/workers`, payload);
    return data;
  },

  async uploadWorkerImage(file: File): Promise<WorkerImageUploadResponse> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<WorkerImageUploadResponse>(
      `${BASE}/uploads`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  async getWorkers(params: GetWorkersParams = {}): Promise<PaginatedResponse<Worker>> {
    const { data } = await apiClient.get<PaginatedResponse<Worker>>(`${BASE}/workers`, {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        ...(params.search ? { search: params.search } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.workerCategory ? { workerCategory: params.workerCategory } : {}),
        ...(params.siteId ? { siteId: params.siteId } : {}),
        ...(params.levelId ? { levelId: params.levelId } : {}),
        ...(params.zoneId ? { zoneId: params.zoneId } : {}),
        ...(params.teamId ? { teamId: params.teamId } : {}),
        ...(params.unassignedOnly ? { unassignedOnly: true } : {}),
      },
    });
    return data;
  },

  async getWorkerById(id: string): Promise<Worker> {
    const { data } = await apiClient.get<Worker>(`${BASE}/workers/${id}`);
    return data;
  },

  async verifyWorker(workerId: string, payload: {
    verificationStage: VerificationStage;
    otp?: string;
    nameMatchConfidence?: number;
    overrideJustification?: string;
  }): Promise<VerifyWorkerResponse> {
    const { data } = await apiClient.post<VerifyWorkerResponse>(
      `${BASE}/workers/${workerId}/verify`,
      payload,
    );
    return data;
  },

  // FR-LBR-003 / §15.2 — Mine Manager/Director approves a Subcontractor-
  // registered worker (PENDING_VERIFICATION → ACTIVE).
  async approveWorker(workerId: string): Promise<Worker> {
    const { data } = await apiClient.patch<Worker>(
      `${BASE}/workers/${workerId}/approve`,
    );
    return data;
  },

  /** The columns of the import file, named for this workspace. */
  async getImportColumns(): Promise<WorkerImportColumn[]> {
    const { data } = await apiClient.get<WorkerImportColumn[]>(`${BASE}/workers/import/columns`);
    return data;
  },

  async downloadImportTemplate(): Promise<Blob> {
    const response = await apiClient.get(`${BASE}/workers/import/template`, {
      responseType: 'blob',
      redirectOnError: false,
    });
    return response.data as Blob;
  },

  /** The server reads the Excel file; a dry run only checks it and saves nothing. */
  async importWorkers(payload: { file: File; dryRun: boolean }): Promise<ImportWorkersReport> {
    const form = new FormData();
    form.append('file', payload.file);
    // A server that has not been updated yet leaves out the new roles; the list is then empty.
    const { data } = await apiClient.post<
      Omit<ImportWorkersReport, 'newRoles' | 'matchedRoles'> &
        Partial<Pick<ImportWorkersReport, 'newRoles' | 'matchedRoles'>>
    >(
      `${BASE}/workers/import`,
      form,
      { params: { dryRun: payload.dryRun }, headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return { ...data, newRoles: data.newRoles ?? [], matchedRoles: data.matchedRoles ?? [] };
  },

  /**
   * Uploads the administrator's payment sheet into the paid-identity reference
   * data. A dry run reports what would change and writes nothing — worth doing
   * on any sheet that re-lists workers already held, because an overwrite
   * replaces a payment-proven phone number and the old value is not kept.
   */
  async importIdentityReferences(payload: {
    file: File;
    dryRun: boolean;
  }): Promise<ImportIdentityReferencesReport> {
    const form = new FormData();
    form.append('file', payload.file);
    const { data } = await apiClient.post<ImportIdentityReferencesReport>(
      `${BASE}/worker-identity-references/import`,
      form,
      { params: { dryRun: payload.dryRun }, headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  async approveWorkers(workerIds: string[]): Promise<ApproveWorkersResult> {
    const { data } = await apiClient.post<ApproveWorkersResult>(`${BASE}/workers/approve`, { workerIds });
    return data;
  },

  async terminateWorker(workerId: string): Promise<Worker> {
    const { data } = await apiClient.patch<Worker>(
      `${BASE}/workers/${workerId}/terminate`,
    );
    return data;
  },

  async updateWorker(workerId: string, payload: UpdateWorkerPayload): Promise<Worker> {
    const { data } = await apiClient.patch<Worker>(
      `${BASE}/workers/${workerId}`,
      payload,
    );
    return data;
  },

  // ── Compensation Preview (FR-CFG-023) ──

  async previewCompensation(payload: {
    periodStart: string;
    periodEnd: string;
    paymentFrequency: string;
  }): Promise<CompensationPreviewResult> {
    const { data } = await apiClient.post<CompensationPreviewResult>(
      `${BASE}/payment-batches/preview`,
      payload,
    );
    return data;
  },

  // ── Attendance Sessions ──

  async createAttendanceSession(payload: CreateAttendanceSessionPayload): Promise<AttendanceSession> {
    const { data } = await apiClient.post<AttendanceSession>(`${BASE}/attendance/sessions`, payload);
    return data;
  },

  async getAttendanceSessions(params: GetAttendanceSessionsParams = {}): Promise<PaginatedResponse<AttendanceSession>> {
    const { data } = await apiClient.get<PaginatedResponse<AttendanceSession>>(`${BASE}/attendance/sessions`, { params });
    return data;
  },

  async getAttendanceSession(id: string): Promise<AttendanceSessionDetail> {
    const { data } = await apiClient.get<AttendanceSessionDetail>(`${BASE}/attendance/sessions/${id}`);
    return data;
  },

  async addWorkersToSession(sessionId: string, workerIds: string[]): Promise<AttendanceEntry[]> {
    const { data } = await apiClient.post<AttendanceEntry[]>(`${BASE}/attendance/sessions/${sessionId}/workers`, { workerIds });
    return data;
  },

  async checkInWorker(
    sessionId: string,
    workerId: string,
    payload: CheckInWorkerPayload = {},
  ): Promise<AttendanceEntry> {
    const { data } = await apiClient.post<AttendanceEntry>(
      `${BASE}/attendance/sessions/${sessionId}/check-in`,
      { workerId, ...payload },
    );
    return data;
  },

  async checkOutWorker(
    sessionId: string,
    workerId: string,
    payload: CheckOutWorkerPayload = {},
  ): Promise<AttendanceEntry> {
    const { data } = await apiClient.post<AttendanceEntry>(
      `${BASE}/attendance/sessions/${sessionId}/check-out`,
      { workerId, ...payload },
    );
    return data;
  },

  // FR-LBR-024 — Mine Manager unlock for a locked attendance record.
  async unlockAttendanceRecord(
    recordId: string,
    justification: string,
  ): Promise<AttendanceEntry> {
    const { data } = await apiClient.patch<AttendanceEntry>(
      `${BASE}/attendance/records/${recordId}/unlock`,
      { justification },
    );
    return data;
  },

  async removeWorkerFromSession(sessionId: string, workerId: string): Promise<void> {
    await apiClient.delete(`${BASE}/attendance/sessions/${sessionId}/workers/${workerId}`);
  },

  async closeAttendanceSession(id: string): Promise<AttendanceSession> {
    const { data } = await apiClient.patch<AttendanceSession>(`${BASE}/attendance/sessions/${id}/close`);
    return data;
  },

  async lockAttendanceSession(id: string): Promise<AttendanceSession> {
    const { data } = await apiClient.patch<AttendanceSession>(`${BASE}/attendance/sessions/${id}/lock`);
    return data;
  },

  // ── Compensation Rules ──

  async getCompensationRules(params: GetCompensationRulesParams = {}): Promise<PaginatedResponse<CompensationRule>> {
    const { data } = await apiClient.get<PaginatedResponse<CompensationRule>>(`${BASE}/compensation/rules`, { params });
    return data;
  },

  async createCompensationRule(payload: CreateCompensationRulePayload): Promise<CompensationRule> {
    const { data } = await apiClient.post<CompensationRule>(`${BASE}/compensation/rules`, payload);
    return data;
  },

  async toggleCompensationRule(id: string): Promise<CompensationRule> {
    const { data } = await apiClient.patch<CompensationRule>(`${BASE}/compensation/rules/${id}/toggle`);
    return data;
  },

  // FR-LBR-044 — edits create a new version row; the old version stays for audit.
  async updateCompensationRule(
    id: string,
    payload: Partial<CreateCompensationRulePayload>,
  ): Promise<CompensationRule> {
    const { data } = await apiClient.patch<CompensationRule>(
      `${BASE}/compensation/rules/${id}`,
      payload,
    );
    return data;
  },

  async getCompensationRuleVersions(id: string): Promise<CompensationRule[]> {
    const { data } = await apiClient.get<CompensationRule[]>(
      `${BASE}/compensation/rules/${id}/versions`,
    );
    return data;
  },

  // FR-LBR-030 — Mine Manager approval workflow.
  async approveCompensationRule(id: string): Promise<CompensationRule> {
    const { data } = await apiClient.patch<CompensationRule>(
      `${BASE}/compensation/rules/${id}/approve`,
    );
    return data;
  },

  async rejectCompensationRule(
    id: string,
    reason: string,
  ): Promise<CompensationRule> {
    const { data } = await apiClient.patch<CompensationRule>(
      `${BASE}/compensation/rules/${id}/reject`,
      { reason },
    );
    return data;
  },

  // ── Subcontractors ──

  async getSubcontractors(params: GetSubcontractorsParams = {}): Promise<PaginatedResponse<Subcontractor>> {
    const { data } = await apiClient.get<PaginatedResponse<Subcontractor>>(`${BASE}/subcontractors`, { params });
    return data;
  },

  async createSubcontractor(payload: CreateSubcontractorPayload): Promise<Subcontractor> {
    const { data } = await apiClient.post<Subcontractor>(`${BASE}/subcontractors`, payload);
    return data;
  },

  async deactivateSubcontractor(id: string): Promise<Subcontractor> {
    const { data } = await apiClient.patch<Subcontractor>(`${BASE}/subcontractors/${id}/deactivate`);
    return data;
  },

  async activateSubcontractor(id: string): Promise<Subcontractor> {
    const { data } = await apiClient.patch<Subcontractor>(`${BASE}/subcontractors/${id}/activate`);
    return data;
  },

  // ── Worker Payment History ──

  async getWorkerPaymentHistory(id: string, params: WorkerPaymentHistoryParams = {}): Promise<WorkerPaymentHistoryResponse> {
    const { data } = await apiClient.get<WorkerPaymentHistoryResponse>(`${BASE}/workers/${id}/payment-history`, { params });
    return data;
  },
};
